import { jsPDF } from 'jspdf';

interface EnergyData {
  jahresPotenzial: number;
  einwohner: number;
  haushalte: number;
  energieQuellen: Array<{
    name: string;
    anzahl: number;
    kwh: number;
    potentialEuro: number;
  }>;
}

interface PDFOptions {
  includeWappen?: boolean;
  includeLogo?: boolean;
  wappenUrl?: string;
}

export async function generatePDFReportWithWappen(
  gemeindeId: string,
  gemeindeName: string,
  data: EnergyData,
  options: PDFOptions = {}
): Promise<void> {
  const {
    includeWappen = true,
    includeLogo = true,
    wappenUrl
  } = options;

  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };
  
  // Colors
  const primaryColor = hexToRgb('#059669'); // Green-600
  const secondaryColor = hexToRgb('#6b7280'); // Gray-500
  const textColor = hexToRgb('#1f2937'); // Gray-800
  
  let yPosition = 20;
  
  // Load and add wappen if requested
  if (includeWappen && wappenUrl) {
    try {
      
      // Convert image to base64 for PDF
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = wappenUrl;
      });
      
      // Create canvas to get base64
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      
      const dataUrl = canvas.toDataURL('image/png');
      
      // Add white background box for wappen
      pdf.setFillColor(255, 255, 255);
      pdf.rect(15, 35, 45, 45, 'F');
      
      // Add wappen to PDF
      pdf.addImage(dataUrl, 'PNG', 20, 40, 35, 35);
    } catch (error) {
      console.error('Failed to load wappen:', error);
    }
  }

  // Header with potential logo placement
  pdf.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b); // Green background
  pdf.rect(0, 0, pageWidth, 30, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ENERGIEGEMEINSCHAFT POTENZIALANALYSE', pageWidth / 2, 20, { align: 'center' });
  
  // Add logo if requested (white version for green background)
  if (includeLogo) {
    // Logo would go on the right side of the header
    // Implementation depends on how the logo is provided
  }
  
  yPosition = 45;
  
  // Gemeinde Name (adjusted position if wappen is present)
  const textStartX = includeWappen ? 70 : 20;
  pdf.setTextColor(textColor.r, textColor.g, textColor.b);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Gemeinde: ${gemeindeName}`, textStartX, yPosition);
  yPosition += 15;
  
  // Date
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(secondaryColor.r, secondaryColor.g, secondaryColor.b);
  pdf.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, textStartX, yPosition);
  yPosition = Math.max(yPosition + 20, 90); // Ensure we're below the wappen
  
  // Main Results Box
  pdf.setFillColor(240, 253, 244); // Light green background
  pdf.setDrawColor(primaryColor.r, primaryColor.g, primaryColor.b);
  pdf.setLineWidth(0.5);
  pdf.rect(15, yPosition - 5, pageWidth - 30, 40, 'FD');
  
  pdf.setTextColor(primaryColor.r, primaryColor.g, primaryColor.b);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('JÄHRLICHES POTENZIAL', 20, yPosition + 5);
  
  pdf.setFontSize(24);
  pdf.text(`€${data.jahresPotenzial.toLocaleString('de-DE')}`, 20, yPosition + 20);
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`€${Math.round(data.jahresPotenzial / data.einwohner)} pro Einwohner`, 20, yPosition + 30);
  pdf.text(`€${Math.round(data.jahresPotenzial / data.haushalte)} pro Haushalt`, 100, yPosition + 30);
  
  yPosition += 55;
  
  // Gemeinde Information
  pdf.setTextColor(textColor.r, textColor.g, textColor.b);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('GEMEINDEDATEN', 20, yPosition);
  yPosition += 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Einwohner: ${data.einwohner.toLocaleString('de-DE')}`, 20, yPosition);
  pdf.text(`Haushalte: ${data.haushalte.toLocaleString('de-DE')}`, 100, yPosition);
  yPosition += 20;
  
  // Energy Sources
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ENERGIEQUELLEN', 20, yPosition);
  yPosition += 15;
  
  // Table Header
  pdf.setFillColor(247, 250, 252);
  pdf.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Energieträger', 20, yPosition);
  pdf.text('Anlagen', 80, yPosition);
  pdf.text('Produktion (kWh)', 120, yPosition);
  pdf.text('Potenzial (€)', 170, yPosition);
  yPosition += 15;
  
  // Table Content
  pdf.setFont('helvetica', 'normal');
  data.energieQuellen.forEach((source, index) => {
    if (yPosition > pageHeight - 30) {
      pdf.addPage();
      yPosition = 20;
    }
    
    // Alternating row colors
    if (index % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
    }
    
    pdf.text(source.name, 20, yPosition);
    pdf.text(source.anzahl.toString(), 80, yPosition);
    pdf.text(source.kwh.toLocaleString('de-DE'), 120, yPosition);
    pdf.text(`€${source.potentialEuro.toLocaleString('de-DE')}`, 170, yPosition);
    yPosition += 12;
  });
  
  yPosition += 10;
  
  // Benefits Section
  if (yPosition > pageHeight - 70) {
    pdf.addPage();
    yPosition = 20;
    
    // Add header for new page
    pdf.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b); // Green background
    pdf.rect(0, 0, pageWidth, 20, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${gemeindeName} - Vorteile & Berechnung`, pageWidth / 2, 13, { align: 'center' });
    
    yPosition = 35;
  }
  
  // Use a very light green instead of opacity
  pdf.setFillColor(230, 252, 240); // Very light green background
  pdf.rect(15, yPosition - 5, pageWidth - 30, 45, 'F');
  
  pdf.setTextColor(textColor.r, textColor.g, textColor.b);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('VORTEILE FÜR IHRE BÜRGER', 20, yPosition + 5);
  yPosition += 15;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('• Durchschnittliche Ersparnis: €147 pro Jahr', 25, yPosition);
  yPosition += 8;
  pdf.text('• Mit eigener PV-Anlage: €384 pro Jahr', 25, yPosition);
  yPosition += 8;
  pdf.text('• Gewerbebetrieb: €1.250 pro Jahr', 25, yPosition);
  yPosition += 20;
  
  // Calculation Formula
  // Check if we need more space
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = 20;
    
    // Add header for new page
    pdf.setFillColor(primaryColor.r, primaryColor.g, primaryColor.b); // Green background
    pdf.rect(0, 0, pageWidth, 20, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${gemeindeName} - Berechnungsgrundlage`, pageWidth / 2, 13, { align: 'center' });
    
    yPosition = 35;
  }
  
  pdf.setTextColor(textColor.r, textColor.g, textColor.b); // Ensure text color is correct
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('BERECHNUNGSGRUNDLAGE', 20, yPosition);
  yPosition += 15;
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Potenzial = Gesamtproduktion × 40% (handelbar) × 0,106 €/kWh', 20, yPosition);
  yPosition += 8;
  pdf.text('40% der produzierten Energie kann durchschnittlich in der', 20, yPosition);
  yPosition += 8;
  pdf.text('Energiegemeinschaft gehandelt werden.', 20, yPosition);
  yPosition += 8;
  pdf.text('0,106 €/kWh entspricht dem durchschnittlichen Marktpreis für', 20, yPosition);
  yPosition += 8;
  pdf.text('regional erzeugte erneuerbare Energie.', 20, yPosition);
  
  // Footer
  const footerY = pageHeight - 20;
  pdf.setFontSize(8);
  pdf.setTextColor(secondaryColor.r, secondaryColor.g, secondaryColor.b);
  pdf.text('Erstellt mit Bürgerstrom Dashboard | www.buergerstrom.at', pageWidth / 2, footerY, { align: 'center' });
  
  // Save the PDF
  pdf.save(`energiegemeinschaft-${gemeindeName.toLowerCase().replace(/\s/g, '-')}.pdf`);
}